'use client'

import { ReactNode } from 'react'
import styled from 'styled-components'

import { Hamburger } from '@syconium/little-miss-figgy/dist/components/Icons/Icon/Hamburger'
import { Help } from '@syconium/little-miss-figgy/dist/components/Icons/Icon/Help'
import { LocaleButton } from '@syconium/little-miss-figgy/dist/components/LocaleButton'
import { ScreenReaderOnly } from '@syconium/little-miss-figgy/dist/components/ScreenReaderOnly'

import {
  NavSubMenuDialogProvider,
  navPushedBackCss,
  useNavSubMenuDialogState,
} from '../../../components/nav/NavSubMenuDialog'
import { usePrimaryNavNestedMenuExperiment } from '../../../components/nav/usePrimaryNavNestedMenuExperiment'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import {
  NextDialogProvider,
  NextDialogTrigger,
  useNextDialogState,
} from '../dialogs/NextDialog.client'
import { NextDrawerDialog, NextDrawerDialogFooter } from '../dialogs/NextDrawerDialog.client'
import { DisclosureSummaryCaratIcon } from '../disclosures/Disclosure.client'
import {
  DisclosureList,
  DisclosureListItem,
  DisclosureListItemContent,
  DisclosureListItemSummary,
} from '../disclosures/DisclosureList.client'

import {
  PrimaryNavContentfulSection,
  PrimaryNavContentfulSectionGap,
} from './PrimaryNavContentfulSections.client'
import { PrimaryNavItem } from './PrimaryNavItemListSection.client'
import { PrimaryNavMenuButton } from './PrimaryNavMenus.client'
import { PrimaryNavOverlayAccordionSection } from './PrimaryNavOverlayAccordionSection.client'
import {
  PrimaryNavTab,
  PrimaryNavTabList,
  PrimaryNavTabPanel,
  PrimaryNavTabProvider,
} from './PrimaryNavTabs.client'

import type { NavSectionsFragmentFragment } from '../../../__generated__/graphql/catalog/graphql'

/**
 * Serializable shape of a single mobile nav tab as returned by the `siteHeader` query. Sections
 * reuse `NavSectionsFragmentFragment` so they render through the same `PrimaryNavContentfulSection`
 * switch used elsewhere.
 */
export type PrimaryNavMobileMenuTab = {
  id: string
  href: string | null
  text: string
  textColor: string | null
  sections: Array<NavSectionsFragmentFragment>
}

// Alternate animated version of LMF's default drawer content wrapper
const PrimaryNavMobileMenuContentWrapper = styled.div`
  ${navPushedBackCss};
`

const PrimaryNavMobileMenuContent = ({ children }: { children?: ReactNode }) => {
  const { isSubMenuOpen } = useNavSubMenuDialogState()

  return (
    <PrimaryNavMobileMenuContentWrapper data-nav-pushed-back={isSubMenuOpen}>
      {children}
    </PrimaryNavMobileMenuContentWrapper>
  )
}

/**
 * The tabbed body of the mobile nav drawer.
 *
 * Split out of `PrimaryNavMobileMenu` so it can read the drawer's dialog store: it renders as a
 * child of `NextDialogProvider`, but above `PrimaryNavTabPanel`, which shadows the dialog context
 * with its own store (see `useNextDialogState`). Closing the nav from below a tab panel therefore
 * has to be passed down rather than read from context.
 */
const PrimaryNavMobileMenuTabs = ({
  accountContent,
  isNestedMenuVariant,
  tabs,
}: {
  accountContent: React.ReactNode
  isNestedMenuVariant: boolean
  tabs: Array<PrimaryNavMobileMenuTab>
}) => {
  const navDialog = useNextDialogState()
  const {
    general: { back: backText, close: closeText },
    navigation: { openSubMenuAriaLabel, accountNavSectionTitle },
  } = useTranslation().magnolia

  return (
    <PrimaryNavTabProvider>
      <header>
        <PrimaryNavTabList>
          {tabs.map(mobileTab => (
            <PrimaryNavTab key={mobileTab.id}>{mobileTab.text}</PrimaryNavTab>
          ))}
        </PrimaryNavTabList>
      </header>
      <PrimaryNavContentfulSectionGap />
      {tabs.map(mobileTab => (
        <PrimaryNavTabPanel key={mobileTab.id}>
          <>
            {mobileTab.sections.map((section, index) => {
              const isAccordion = section.__typename === 'NavAccordion'
              const isLast = index === mobileTab.sections.length - 1
              const nextIsAccordion = isLast
                ? true
                : mobileTab.sections[index + 1]?.__typename === 'NavAccordion'
              const renderGap = !isAccordion || !nextIsAccordion
              const gutterStyle = { padding: '0px 16px' }
              return (
                <div key={index} style={!isAccordion ? gutterStyle : undefined}>
                  {/*
                    Branching here rather than inside `PrimaryNavContentfulSection` keeps the
                    desktop hovercard menus, which share that switch, on the control rendering.
                  */}
                  {isAccordion && isNestedMenuVariant ? (
                    <PrimaryNavOverlayAccordionSection
                      {...section}
                      backText={backText}
                      closeText={closeText}
                      onCloseNav={navDialog.hide}
                      openSubMenuAriaLabel={openSubMenuAriaLabel}
                    />
                  ) : (
                    <PrimaryNavContentfulSection {...section} />
                  )}
                  {renderGap ? <PrimaryNavContentfulSectionGap /> : null}
                </div>
              )
            })}
            <div>
              <DisclosureList>
                <DisclosureListItem>
                  <DisclosureListItemSummary renderIcon={<DisclosureSummaryCaratIcon />}>
                    <PrimaryNavItem asTag='span' $variant='heading'>
                      <span>{accountNavSectionTitle}</span>
                    </PrimaryNavItem>
                  </DisclosureListItemSummary>
                  <DisclosureListItemContent>{accountContent}</DisclosureListItemContent>
                </DisclosureListItem>
              </DisclosureList>
            </div>
          </>
        </PrimaryNavTabPanel>
      ))}
    </PrimaryNavTabProvider>
  )
}

/**
 * Mobile navigation drawer, relocated out of the server `PrimaryNavHeaderMainMenu` so that A/B tests can be evaluated at a client boundary.
 */
export const PrimaryNavMobileMenu = ({
  tabs,
  helpLinkHref,
  helpLinkText,
  accountContent,
  localizationDialog,
}: {
  tabs: Array<PrimaryNavMobileMenuTab>
  helpLinkHref: string | null
  helpLinkText: string | null
  accountContent: React.ReactNode
  localizationDialog: React.ReactNode
}) => {
  const {
    general: { close: closeText },
    navigation: { navigationTitle, hamburgerAriaLabel },
  } = useTranslation().magnolia

  // TODO: Remove if NestedMenuNav test is successful
  const { isNestedMenuVariant } = usePrimaryNavNestedMenuExperiment()

  return (
    <NavSubMenuDialogProvider>
      <NextDialogProvider>
        <NextDialogTrigger render={<PrimaryNavMenuButton />} toggleOnClick>
          <Hamburger aria-hidden='true' />
          <ScreenReaderOnly>{hamburgerAriaLabel}</ScreenReaderOnly>
        </NextDialogTrigger>
        <NextDrawerDialog
          analyticsTitle='Navigation'
          content={<PrimaryNavMobileMenuContent />}
          variant='right-drawer'
          closeText={closeText}
          aria-label={navigationTitle}
          closeOnSearchParamChange
        >
          <PrimaryNavMobileMenuTabs
            accountContent={accountContent}
            isNestedMenuVariant={isNestedMenuVariant}
            tabs={tabs}
          />
          <NextDrawerDialogFooter>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {localizationDialog}
              {helpLinkHref && helpLinkText ? (
                <LocaleButton forwardedAs='a' isColorInverted={false} href={helpLinkHref}>
                  <Help />
                  {helpLinkText}
                </LocaleButton>
              ) : null}
            </div>
          </NextDrawerDialogFooter>
        </NextDrawerDialog>
      </NextDialogProvider>
    </NavSubMenuDialogProvider>
  )
}
